import React, { useEffect } from 'react';
import Head from 'next/head';
import { IntercomProvider } from 'react-use-intercom';
import { Provider } from 'react-redux';
import type { AppProps } from 'next/app';
import { PersistGate } from 'redux-persist/integration/react';
import { FlagProvider } from '@unleash/proxy-client-react';

import SafeHydrate from '@atoms/SafeHydrate';
import { initialiseAnalytics } from '@utils/Analytics';
import store, { persistor } from '@context/store';
import ReactQueryProvider from '@context/Query';
import { AuthProvider } from '@context/Auth';
import MicrosoftSSOProvider from '@context/Msal';
// Carbon base styles - don't update too often unless you want the local build grinding to a halt
import '../styles/index.scss';

// Pawprint styles
import './_app.scss';

const intercomAppId = process.env.NEXT_PUBLIC_INTERCOM_KEY as string;

// Unleash feature flagging config
const unleashConfig = {
  url: process.env.NEXT_PUBLIC_UNLEASH_URL,
  clientKey: process.env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  appName: 'unused-via-unleash-proxy-but-required',
};

function App({ Component, pageProps }: AppProps) {
  useEffect(() => {
    initialiseAnalytics();
  }, []);

  return (
    <IntercomProvider appId={intercomAppId} autoBoot>
      <Head>
        <title>Pawprint dashboard</title>
      </Head>
      <SafeHydrate>
        <FlagProvider config={unleashConfig}>
          <ReactQueryProvider>
            <AuthProvider>
              <MicrosoftSSOProvider>
                <Provider store={store}>
                  <PersistGate loading={null} persistor={persistor}>
                    <Component {...pageProps} />
                  </PersistGate>
                </Provider>
              </MicrosoftSSOProvider>
            </AuthProvider>
          </ReactQueryProvider>
        </FlagProvider>
      </SafeHydrate>
    </IntercomProvider>
  );
}

export default App;
